import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { RecordingModal } from './RecordingModal';

export const VoiceSelector = ({ 
  userId, 
  scrollId, 
  scrollTitle,
  scrollText,
  onVoiceChange 
}) => {
  const [selectedVoice, setSelectedVoice] = useState('none');
  const [voiceVolume, setVoiceVolume] = useState(1.0);
  const [hasRecording, setHasRecording] = useState(false);
  const [recordingUrl, setRecordingUrl] = useState(null);
  const [showRecordingModal, setShowRecordingModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [userPreference, setUserPreference] = useState(null);

  useEffect(() => {
    checkForRecording();
    loadUserPreference();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, scrollId]);

  // Trigger onVoiceChange when recording URL is available and voice is set to own-voice
  useEffect(() => {
    if (selectedVoice === 'own-voice' && recordingUrl && !isLoading) {
      onVoiceChange({ type: 'own-voice', url: recordingUrl, volume: voiceVolume });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordingUrl, selectedVoice, isLoading]);

  const loadUserPreference = async () => {
    if (!userId) return;

    try {
      const { data } = await supabase
        .from('user_profiles')
        .select('audio_preferences')
        .eq('id', userId)
        .single();

      if (data && data.audio_preferences) {
        const prefs = data.audio_preferences;
        if (prefs.voice_volume !== undefined) {
          setVoiceVolume(prefs.voice_volume);
        }
        if (prefs.preferred_voice) {
          setSelectedVoice(prefs.preferred_voice);
        }
      }
    } catch (err) {
      console.error('Error loading voice preference:', err);
    }
  };

  const checkForRecording = async () => {
    if (!userId || !scrollId) return;
    
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('user_recordings')
        .select('recording_url')
        .eq('user_id', userId)
        .eq('scroll_id', scrollId)
        .single();

      if (data && !error) {
        console.log('Found existing recording:', data.recording_url);
        setHasRecording(true);
        setRecordingUrl(data.recording_url);
      } else {
        setHasRecording(false);
        setRecordingUrl(null);
      }
    } catch (err) {
      console.error('Error checking for recording:', err);
    }
    setIsLoading(false);
  };

  const handleVoiceChange = async (e) => {
    const value = e.target.value;
    setSelectedVoice(value);

    if (value === 'own-voice') {
      if (!hasRecording) {
        setShowRecordingModal(true);
      } else {
        onVoiceChange({ type: 'own-voice', url: recordingUrl, volume: voiceVolume });
      }
    } else if (value === 'none') {
      onVoiceChange({ type: 'none', url: null, volume: voiceVolume });
    } else {
      onVoiceChange({ type: value, volume: voiceVolume });
    }

    // Save preference
    await saveVoicePreference(value);
  };

  const handleVolumeChange = async (e) => {
    const volume = parseFloat(e.target.value);
    setVoiceVolume(volume);

    if (selectedVoice === 'own-voice' && recordingUrl) {
      onVoiceChange({ type: 'own-voice', url: recordingUrl, volume });
    } else {
      onVoiceChange({ type: selectedVoice, volume });
    }

    await savePreference(volume);
  };

  const savePreference = async (volume) => {
    if (!userId) return;

    try {
      const newPrefs = {
        ...(userPreference || {}),
        voice_volume: volume
      };

      const { error } = await supabase
        .from('user_profiles')
        .update({ audio_preferences: newPrefs })
        .eq('id', userId);

      if (error) throw error;
      setUserPreference(newPrefs);
    } catch (err) {
      console.error('Error saving voice preference:', err);
    }
  };

  const saveVoicePreference = async (voiceType) => {
    if (!userId) return;

    try {
      const newPrefs = {
        ...(userPreference || {}),
        preferred_voice: voiceType
      };

      const { error } = await supabase
        .from('user_profiles')
        .update({ audio_preferences: newPrefs })
        .eq('id', userId);

      if (error) throw error;
      setUserPreference(newPrefs);
    } catch (err) {
      console.error('Error saving voice preference:', err);
    }
  };

  const handleRecordingComplete = (url) => {
    setHasRecording(true);
    setRecordingUrl(url);
    setSelectedVoice('own-voice');
    onVoiceChange({ type: 'own-voice', url, volume: voiceVolume });
  };

  const handleReRecord = () => {
    setShowRecordingModal(true);
  };

  return (
    <div style={styles.container}>
      <label style={styles.label}>Voice Selection</label>
      
      <div style={styles.selectContainer}>
        <select
          value={selectedVoice}
          onChange={handleVoiceChange}
          disabled={isLoading}
          style={styles.select}
        >
          <option value="none">None (Silent Reading)</option>
          
          <option value="own-voice">
            {hasRecording ? '✓ My Own Voice' : 'My Own Voice'}
          </option>
          
          <option value="ai-voice-1" disabled>
            Professional Voice 1 (Coming Soon)
          </option>
          <option value="ai-voice-2" disabled>
            Professional Voice 2 (Coming Soon)
          </option>
          <option value="ai-voice-3" disabled>
            Professional Voice 3 (Coming Soon)
          </option>
          <option value="ai-voice-4" disabled>
            Professional Voice 4 (Coming Soon)
          </option>
        </select>

        {selectedVoice === 'own-voice' && hasRecording && (
          <button onClick={handleReRecord} style={styles.reRecordButton} title="Re-record your voice">
            🎤
          </button>
        )}
      </div>
      
      {selectedVoice === 'own-voice' && !hasRecording && (
        <div style={styles.hintContainer}>
          <p style={styles.hint}>
            🎤 No recording yet
          </p>
          <button onClick={() => setShowRecordingModal(true)} style={styles.recordButton}>
            Record Now
          </button>
        </div>
      )}

      <RecordingModal
        isOpen={showRecordingModal}
        onClose={() => setShowRecordingModal(false)}
        scrollText={scrollText}
        scrollTitle={scrollTitle}
        userId={userId}
        scrollId={scrollId}
        onSaveComplete={handleRecordingComplete}
      />
    </div>
  );
};

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  label: {
    display: 'block',
    fontSize: '0.875rem',
    fontWeight: '500',
    color: '#cbd5e1',
  },
  selectContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  select: {
    flex: 1,
    padding: '0.8571em',
    border: '1px solid #2a3142',
    borderRadius: '8px',
    fontSize: '0.875rem',
    backgroundColor: '#0f1419',
    color: '#e2e8f0',
    cursor: 'pointer',
  },
  reRecordButton: {
    backgroundColor: 'transparent',
    border: '1px solid #3b82f6',
    color: '#3b82f6',
    padding: '0.4444em 0.6667em',
    borderRadius: '8px',
    fontSize: '1.125rem',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  volumeContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '8px',
  },
  volumeIcon: {
    fontSize: '1.125rem',
    color: '#94a3b8',
  },
  volumeSlider: {
    flex: 1,
    height: '6px',
    background: '#2a3142',
    borderRadius: '3px',
    outline: 'none',
    cursor: 'pointer',
  },
  volumeValue: {
    fontSize: '0.75rem',
    color: '#cbd5e1',
    minWidth: '40px',
    textAlign: 'right',
  },
  hint: {
    fontSize: '0.75rem',
    color: '#94a3b8',
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  hintContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
  },
  recordButton: {
    padding: '0.6154em 1.2308em',
    backgroundColor: '#3b82f6',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '0.8125rem',
    fontWeight: '600',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
};